'use client';

import { useMemo, useState } from 'react';
import { type FormatDateOptions, useIntl } from 'react-intl';
import logError from '@alltrails/core/utils/logError';
import Button from '@alltrails/denali/components/Button';
import { NewsroomItem, NewsroomItemsResponse } from '@/types/newsroom';
import getNewsroomItems from '@/utils/getNewsroomItems';
import { buildPostHref, getCategoryDisplayName } from '@/utils/newsroomHelpers';
import CategoryFilter, { type FilterOption } from '../CategoryFilter/CategoryFilter';
import NewsroomCard from '../NewsroomCard/NewsroomCard';
import styles from './NewsRoomBody.module.scss';

// Designs https://www.figma.com/design/Y27OVUVzKrRbVqPXo5MVmG/Newsroom-Phase-II?node-id=2952-11943&m=dev

const formatOptions: FormatDateOptions = { month: 'short', day: 'numeric', year: 'numeric' };

interface NewsRoomBodyProps {
  initialData: NewsroomItemsResponse | null;
}

export default function NewsRoomBody({ initialData }: NewsRoomBodyProps) {
  const [posts, setPosts] = useState<NewsroomItem[]>(initialData?.posts || []);
  const [nextCursor, setNextCursor] = useState<string | undefined>(initialData?.next_cursor);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState<FilterOption>('all');

  const loadMorePosts = async () => {
    if (!nextCursor || isLoading) {
      return;
    }

    setIsLoading(true);
    try {
      const response = await getNewsroomItems(nextCursor);
      setPosts(prevPosts => [...prevPosts, ...response.posts]);
      setNextCursor(response.next_cursor);
    } catch (error) {
      logError(new Error('Failed to load more posts', { cause: error }));
    } finally {
      setIsLoading(false);
    }
  };

  const filteredPosts = useMemo(() => {
    return selectedFilter === 'all' ? posts : posts.filter(post => post.category_slug === selectedFilter);
  }, [posts, selectedFilter]);

  const intl = useIntl();

  return (
    <div className={styles.container}>
      <div className={styles.contentContainer}>
        <CategoryFilter selectedCategory={selectedFilter} setCategory={setSelectedFilter} className={styles.categories} />
        <div className={styles.grid}>
          {filteredPosts?.length > 0 &&
            filteredPosts.map(item => (
              <NewsroomCard
                key={item.id}
                thumbnail={item.hero_image?.full}
                category={getCategoryDisplayName(intl, item.category_slug)}
                title={item.title}
                date={intl.formatDate(item.date, formatOptions)}
                href={buildPostHref(item.slug, item.type)}
                is_sponsored={item.is_sponsored}
                status={item.status}
              />
            ))}
        </div>
        {nextCursor && (
          <div className={styles.loadMoreContainer}>
            <Button text={intl.formatMessage({ defaultMessage: 'Load more' })} onClick={loadMorePosts} disabled={isLoading} />
          </div>
        )}
      </div>
    </div>
  );
}
